import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getMaxUploadSizeByChannel } from '@chatwoot/utils';
import {
  AccountScopedDirectUpload,
  setDirectUploadAuthHeaders,
} from 'dashboard/helper/directUploadsHelper';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { convertSelectedAudioToMp3 } from 'dashboard/components/widgets/WootWriter/utils/audioConversionUtils';

const MAX_PARALLEL_PREVIEW_SIZE_BYTES = 1024 * 1024;

export default {
  data() {
    return {
      pendingDirectUploads: 0,
      pendingAudioConversions: 0,
      uploadContextRevision: 0,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    installationLimit() {
      return resolveMaximumFileUploadSize(
        this.globalConfig.maximumFileUploadSize
      );
    },
  },

  watch: {
    accountId: 'invalidateUploadContext',
    'currentChat.id': 'invalidateUploadContext',
    isOnPrivateNote: 'invalidateUploadContext',
  },
  beforeUnmount() {
    this.invalidateUploadContext();
  },

  methods: {
    invalidateUploadContext() {
      this.uploadContextRevision += 1;
    },
    captureUploadContext() {
      const revision = this.uploadContextRevision;
      const accountId = this.accountId;
      const conversationId = this.currentChat.id;
      const isPrivate = this.isOnPrivateNote;
      return () =>
        revision === this.uploadContextRevision &&
        accountId === this.accountId &&
        conversationId === this.currentChat.id &&
        isPrivate === this.isOnPrivateNote;
    },
    readImagePreview(file) {
      if (!file?.type?.startsWith('image/')) return Promise.resolve('');

      return new Promise(resolve => {
        const reader = new FileReader();
        const finish = () => resolve(reader.result || '');
        reader.onloadend = finish;
        reader.onerror = finish;
        reader.onabort = finish;
        try {
          reader.readAsDataURL(file);
        } catch {
          finish();
        }
      });
    },
    async convertAttachmentToMp3(attachment) {
      if (
        this.pendingAudioConversions ||
        !this.attachedFiles.includes(attachment)
      )
        return;
      const original = attachment.originalFile;
      if (!original || !/\.ogg$/i.test(original.name)) return;
      const isCurrent = this.captureUploadContext();
      const canReplace = () =>
        isCurrent() && this.attachedFiles.includes(attachment);
      this.pendingAudioConversions += 1;
      attachment.isConverting = true;
      try {
        const audio = await convertSelectedAudioToMp3(original);
        if (!canReplace()) return;
        const mp3 = new File(
          [audio],
          original.name.replace(/\.ogg$/i, '.mp3'),
          {
            type: 'audio/mpeg',
          }
        );
        const upload = this.globalConfig.directUploadsEnabled
          ? this.onDirectFileUpload
          : this.onIndirectFileUpload;
        await upload(
          { file: mp3, name: mp3.name, type: mp3.type, size: mp3.size },
          ({ file, blob }) => {
            if (!canReplace()) return;
            this.attachedFiles.splice(
              this.attachedFiles.indexOf(attachment),
              1,
              {
                ...attachment,
                resource: blob || file,
                blobSignedId: blob?.signed_id,
                thumb: '',
                isVoiceMessage: false,
                isConverting: false,
              }
            );
          }
        );
      } catch (error) {
        if (canReplace()) {
          useAlert(
            error instanceof RangeError
              ? this.$t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_TOO_LARGE')
              : this.$t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED')
          );
        }
      } finally {
        attachment.isConverting = false;
        this.pendingAudioConversions -= 1;
      }
    },
    maxSizeFor(mime) {
      // Use default/installation limit for private notes
      if (this.isOnPrivateNote) {
        return this.installationLimit;
      }

      const channelType = this.inbox?.channel_type;

      if (!channelType || channelType === INBOX_TYPES.WEB) {
        return this.installationLimit;
      }

      const channelLimit = getMaxUploadSizeByChannel({
        channelType,
        medium: this.inbox?.medium, // e.g. 'sms' | 'whatsapp'
        mime, // e.g. 'image/png'
      });

      if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
        return this.installationLimit;
      }

      return Math.min(channelLimit, this.installationLimit);
    },
    alertOverLimit(maxSizeMB) {
      useAlert(
        this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
        })
      );
    },
    onFileUpload(file) {
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file);
      } else {
        this.onIndirectFileUpload(file);
      }
    },

    onDirectFileUpload(file, onAttached = this.attachFile) {
      if (!file) return undefined;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return undefined;
      }

      let preview;
      const preparePreview = () => {
        preview ||= this.readImagePreview(file.file);
        return preview;
      };
      const preparePreviewDuringUpload = () => {
        if (file.file.size <= MAX_PARALLEL_PREVIEW_SIZE_BYTES) preparePreview();
      };
      const upload = new AccountScopedDirectUpload(
        file.file,
        `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            setDirectUploadAuthHeaders(xhr);
          },
          directUploadWillStoreFileWithXHR: preparePreviewDuringUpload,
        }
      );

      const isCurrent = this.captureUploadContext();
      return new Promise(resolve => {
        this.pendingDirectUploads += 1;
        upload.create((error, blob) => {
          const finish = thumb => {
            this.pendingDirectUploads -= 1;
            if (isCurrent()) {
              if (error) useAlert(error);
              else onAttached({ file, blob, thumb });
            }
            resolve(!error && isCurrent());
          };

          if (error) finish('');
          else preparePreview().then(finish);
        });
      });
    },

    onIndirectFileUpload(file, onAttached = this.attachFile) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      onAttached({ file });
    },
  },
};
